import { Component } from '@theme/component';

const STORAGE_SUBSCRIBED = 'newsletter-popup-subscribed';
const STORAGE_DISMISSED_UNTIL = 'newsletter-popup-dismissed-until';
const STORAGE_PENDING = 'newsletter-popup-pending-submission';
const PENDING_WINDOW_MS = 60 * 1000;

/**
 * A custom element that manages the subtle, non-blocking newsletter signup
 * popup shown on product pages. Mirrors cookie-consent banner behavior:
 * shows once (after a delay), remembers a close for a merchant-configurable
 * cooldown, and never shows again once the visitor actually subscribes.
 *
 * The submission itself is left completely native/un-intercepted, with a
 * `return_to` hidden field added so Shopify's redirect lands back on this
 * exact product page (confirmed live — without it, submitting did not
 * reliably return here).
 *
 * Success detection deliberately does NOT inspect the reloaded page for
 * Shopify's own `Email-signup__message-success-*` marker (what the very
 * first version of this file did, and what the footer's identical form
 * relies on by default). Confirmed live: a real subscription can succeed
 * (confirmation email received) while that marker is absent from the page
 * `return_to` redirects back to — the marker only appears to survive
 * Shopify's *default* redirect, not one steered by an explicit `return_to`.
 * Since Shopify's storefront CAPTCHA requirement also blocks any scripted
 * way of inspecting the real POST/redirect response directly (confirmed
 * live), there's no reliable signal left to read from the response itself.
 * Instead, a pending flag is set in localStorage the moment the native
 * submit fires (before Shopify even processes it), and treated as a
 * completed, successful submission if it's still there — and recent — when
 * the popup's component reconnects after the page reloads back here.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} panel
 * @property {HTMLButtonElement} closeButton
 *
 * @extends {Component<Refs>}
 */
class NewsletterPopupComponent extends Component {
  requiredRefs = ['panel', 'closeButton'];

  /** @type {number | undefined} */
  #timeoutId;

  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    super.connectedCallback();

    this.refs.closeButton.addEventListener('click', this.#handleClose);

    this.#form = this.querySelector('.email-signup__form');
    if (this.#form) {
      this.#ensureReturnsToThisPage(this.#form);
      this.#form.addEventListener('submit', this.#handleSubmit);
    }

    this.#maybeAddPreviewButton();

    if (this.#consumePendingSubmission()) {
      this.#setSubscribed();
      this.#renderSuccessMessage();
      this.#show();
      return;
    }

    if (this.#isSubscribed() || this.#isCoolingDown()) return;

    const delaySeconds = Number(this.dataset.showDelay) || 0;
    this.#timeoutId = window.setTimeout(() => this.#show(), delaySeconds * 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timeoutId) window.clearTimeout(this.#timeoutId);
    this.refs.closeButton.removeEventListener('click', this.#handleClose);
    if (this.#form) this.#form.removeEventListener('submit', this.#handleSubmit);
  }

  #handleClose = () => {
    this.#setDismissed();
    this.#hide();
  };

  /** @param {HTMLFormElement} form */
  #ensureReturnsToThisPage(form) {
    let returnTo = form.querySelector('input[name="return_to"]');
    if (!(returnTo instanceof HTMLInputElement)) {
      returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      form.appendChild(returnTo);
    }
    returnTo.value = window.location.pathname + window.location.search;
  }

  // Left deliberately native (no preventDefault) — Shopify's CAPTCHA
  // requirement on this endpoint means anything else gets rejected. This
  // only records that a submission is in flight, right before the browser
  // takes over.
  #handleSubmit = () => {
    try {
      window.localStorage.setItem(STORAGE_PENDING, String(Date.now()));
    } catch (error) {
      // localStorage unavailable — the returning page just won't detect
      // success automatically; not fatal.
    }
  };

  #consumePendingSubmission() {
    try {
      const pendingSince = Number(window.localStorage.getItem(STORAGE_PENDING));
      window.localStorage.removeItem(STORAGE_PENDING);
      return Number.isFinite(pendingSince) && Date.now() - pendingSince < PENDING_WINDOW_MS;
    } catch (error) {
      return false;
    }
  }

  #renderSuccessMessage() {
    // Hide everything the merchant put in the popup's content (heading,
    // subtext, the sign-up form) and show only the plain success message
    // in its place — no leftover form scaffolding.
    const content = this.querySelector('.newsletter-popup__content');
    if (content instanceof HTMLElement) content.hidden = true;

    let messageEl = this.querySelector('.newsletter-popup__message');
    if (!messageEl) {
      messageEl = document.createElement('div');
      messageEl.className = 'newsletter-popup__message';
      this.refs.panel.appendChild(messageEl);
    }
    messageEl.textContent = this.dataset.successMessage || 'Thanks for subscribing!';
  }

  #show() {
    this.setAttribute('open', '');
  }

  #hide() {
    this.removeAttribute('open');
  }

  #isSubscribed() {
    try {
      return window.localStorage.getItem(STORAGE_SUBSCRIBED) === 'true';
    } catch (error) {
      return false;
    }
  }

  #setSubscribed() {
    try {
      window.localStorage.setItem(STORAGE_SUBSCRIBED, 'true');
    } catch (error) {
      // localStorage unavailable (private browsing, blocked) — fail silently
    }
  }

  #isCoolingDown() {
    try {
      const dismissedUntil = Number(window.localStorage.getItem(STORAGE_DISMISSED_UNTIL));
      return Number.isFinite(dismissedUntil) && Date.now() < dismissedUntil;
    } catch (error) {
      return false;
    }
  }

  #setDismissed() {
    try {
      const cooldownDays = Number(this.dataset.cooldownDays) || 7;
      window.localStorage.setItem(STORAGE_DISMISSED_UNTIL, String(Date.now() + cooldownDays * 24 * 60 * 60 * 1000));
    } catch (error) {
      // localStorage unavailable — closing still hides it for this page view
    }
  }

  // Merchant-only preview affordance: only rendered inside the theme editor
  // (window.Shopify.designMode), never for real visitors. Lets the merchant
  // see the success message instantly without submitting a real email.
  #maybeAddPreviewButton() {
    if (!window.Shopify?.designMode) return;
    if (this.querySelector('.newsletter-popup__test-button')) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'newsletter-popup__test-button';
    button.textContent = 'Preview success message';
    button.addEventListener('click', () => {
      this.#show();
      this.#renderSuccessMessage();
    });
    this.refs.panel.appendChild(button);
  }
}

if (!customElements.get('newsletter-popup-component')) {
  customElements.define('newsletter-popup-component', NewsletterPopupComponent);
}
